import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "@/store/DataContext";
import {
  ChipGroup,
  Field,
  PrimaryButton,
  TextArea,
  TextInput,
} from "@/components/base/FormControls";
import SelectSheet, { type SelectOption } from "@/components/base/SelectSheet";
import DocumentThumb from "@/components/feature/DocumentThumb";
import Icon3D from "@/components/base/Icon3D";
import { formatDate, todayISO, uid } from "@/lib/format";
import type { DocumentRecord, MovementType } from "@/lib/types";

const docTypes = [
  { key: "factura", label: "Factura", icon: "ri-receipt-line" },
  { key: "evidencia", label: "Evidencia", icon: "ri-image-line" },
  { key: "comprobante", label: "Comprobante", icon: "ri-file-check-line" },
  { key: "otro", label: "Otro", icon: "ri-file-line" },
];

const linkLabels: Record<string, string> = {
  general: "General",
  purchase: "Compra",
  sale: "Venta",
  expense: "Gasto",
  return: "Devolución",
};

export default function NuevoDocumento() {
  const { data, timeline, addDocument } = useData();
  const navigate = useNavigate();

  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [docType, setDocType] = useState<DocumentRecord["docType"]>("factura");
  const [date, setDate] = useState(todayISO());
  const [supplier, setSupplier] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [linkId, setLinkId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const movementOptions: SelectOption[] = useMemo(
    () => [
      { value: "", label: "Sin asociar", sub: "Documento general" },
      ...timeline.map((entry) => ({
        value: entry.id,
        label: entry.title,
        sub: `${linkLabels[entry.kind]} · ${formatDate(entry.date)}`,
      })),
    ],
    [timeline],
  );

  const linkedEntry = timeline.find((e) => e.id === linkId);
  const linkType: MovementType | "general" = linkedEntry
    ? (linkedEntry.kind as MovementType)
    : "general";

  const handleFile = (selected: File | null) => {
    if (!selected) return;
    setFile(selected);
    if (!title.trim()) {
      setTitle(selected.name.replace(/\.[^.]+$/, ""));
    }
  };

  const save = async () => {
    if (!file) {
      setError("Adjunta una foto o un archivo PDF de la factura.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const doc: DocumentRecord = {
        id: uid(),
        title: title.trim() || "Documento sin título",
        description: description.trim(),
        docType,
        linkType,
        linkId: linkId || null,
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        size: file.size,
        date,
        supplier: supplier.trim(),
        invoiceNumber: invoiceNumber.trim(),
        blob: file,
        createdAt: new Date().toISOString(),
      };
      await addDocument(doc);
      navigate("/documentos");
    } catch (err) {
      setError("No se pudo guardar el documento. Intenta de nuevo.");
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fr-animate space-y-5 pb-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-background-200/80 text-foreground-700"
        >
          <i className="ri-arrow-left-line text-lg" />
        </button>
        <div>
          <h1 className="text-[20px] font-bold text-foreground-950">Escanear factura</h1>
          <p className="text-[12px] text-foreground-500">Toma una foto o adjunta un PDF</p>
        </div>
      </div>

      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
      />

      {file ? (
        <div className="overflow-hidden rounded-3xl border border-background-200/70 bg-background-50 shadow-card">
          <DocumentThumb
            blob={file}
            mimeType={file.type}
            className="h-48 w-full"
            iconClass="text-5xl"
          />
          <div className="flex items-center justify-between p-3">
            <span className="truncate text-[12px] text-foreground-600">{file.name}</span>
            <button
              type="button"
              onClick={() => setFile(null)}
              className="flex h-7 w-7 items-center justify-center rounded-full bg-background-200 text-foreground-500"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            className="flex flex-col items-center justify-center gap-2 rounded-3xl border border-background-200/70 bg-background-50 py-6 shadow-card active:scale-[0.98]"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-100">
              <Icon3D icon="ri-camera-line" tone="primary" size={26} />
            </span>
            <span className="text-[12px] font-medium text-foreground-800">Tomar foto</span>
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex flex-col items-center justify-center gap-2 rounded-3xl border border-background-200/70 bg-background-50 py-6 shadow-card active:scale-[0.98]"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary-100">
              <Icon3D icon="ri-image-add-line" tone="secondary" size={26} />
            </span>
            <span className="text-[12px] font-medium text-foreground-800">Elegir archivo</span>
          </button>
        </div>
      )}

      <Field label="Título del documento">
        <TextInput
          value={title}
          onChange={setTitle}
          placeholder="Ej. Factura de bocinas"
          icon="ri-text"
        />
      </Field>

      <Field label="Tipo de documento">
        <ChipGroup options={docTypes} value={docType} onChange={(v) => setDocType(v as DocumentRecord["docType"])} />
      </Field>

      <Field label="Asociar a un movimiento (opcional)">
        <SelectSheet
          value={linkId}
          options={movementOptions}
          placeholder="Sin asociar"
          title="Asociar documento"
          onChange={setLinkId}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Fecha">
          <TextInput value={date} onChange={setDate} type="date" icon="ri-calendar-line" />
        </Field>
        <Field label="N.º de factura">
          <TextInput
            value={invoiceNumber}
            onChange={setInvoiceNumber}
            placeholder="Ej. F-0012"
            icon="ri-file-list-3-line"
          />
        </Field>
      </div>

      <Field label="Proveedor (opcional)">
        <TextInput
          value={supplier}
          onChange={setSupplier}
          placeholder="Nombre del proveedor"
          icon="ri-store-2-line"
        />
      </Field>

      <Field label="Descripción (opcional)">
        <TextArea
          value={description}
          onChange={setDescription}
          placeholder="¿A qué corresponde este documento?"
        />
      </Field>

      {error ? (
        <p className="rounded-2xl bg-red-50 px-4 py-3 text-[12px] text-red-600">{error}</p>
      ) : null}

      <PrimaryButton
        label={saving ? "Guardando..." : "Guardar documento"}
        icon="ri-save-3-line"
        onClick={save}
        disabled={saving}
      />
    </div>
  );
}